import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { connect, useSelector } from 'react-redux';
import type { RouteComponentProps } from 'react-router-dom';
import { withRouter, useHistory, useLocation } from 'react-router-dom';
import type { MenuItem as HudskitMenuItem } from 'hfs-common-ui';
import { NavBar, NavMenu, breakpoints } from 'hfs-common-ui';
import type { AppState } from '~/reducers';
import { toggleYoutubePlaylistModalOpen as toggleYoutubePlaylistModalOpenAction } from '~/components/containers/YoutubePlaylistContainer/actions';
import MenuConstants from '~/constants/menu-constants';
import useBooleanState from '~/hooks/use-boolean-state';
import useMediaQuery from '~/hooks/use-media-query';
import UptimeCompanyInfo from '~/domain/uptime/components/UptimeCompanyInfo/UptimeCompanyInfo';
import { trimUptimePath } from '~/domain/uptime/utils/utils';
import { useDiamAccountPortalUrl } from '~/domain/users/hooks/use-diam-account-portal-url';
import { greenkeeperMobileSubmenu } from '~/domain/greenkeeper-tool/utils/greenkeeper-navigation';
import UserPreferencesSubmenu from '~/components/containers/MenubarContainer/components/UserPreferencesSubmenu/UserPreferencesSubmenu';
import { useUptime } from '~/domain/uptime/hooks/use-uptime';
import {
  useGetMeasurementSystemPreferenceEnabled,
  useGetGreenkeeperEnabled,
} from '~/config/firebase/remote-config';
import DevScreenSizeIcon from './components/DevScreenSizeIcon/DevScreenSizeIcon';
import NavbarLogo from './components/NavbarLogo/NavbarLogo';
import type { FleetMenuItem } from './types/types';
import {
  composeRightSection,
  hasMenuMetadata,
  isLinkMenuItem,
  isOutLinkMenuItem,
  mapNavMenu,
} from './utils';
import DropdownNavMenu from './components/DropdownNavMenu/DropdownNavMenu';
import MobileMenuDrawer from './components/MobileMenuDrawer/MobileMenuDrawer';
import MobileMenu from './components/MobileMenu/MobileMenu';
import MobileRightMenu from './components/MobileRightMenu/MobileRightMenu';
import type { MenuItem } from './data/MenuState';
import * as S from './styled';

export interface StateProps {
  leftSection: readonly MenuItem[];
}

export interface DispatchProps {
  toggleYoutubePlaylistModalOpen: typeof toggleYoutubePlaylistModalOpenAction;
}
const secondaryRightMenu = [MenuConstants.INFORMATION, MenuConstants.USER];

const menuItemPath = (item: FleetMenuItem): string | undefined =>
  isLinkMenuItem(item.metadata) ? item.metadata.path : undefined;

export type Props = StateProps & DispatchProps;

export const MenubarContainer = ({ leftSection, toggleYoutubePlaylistModalOpen }: Props) => {
  const { utcMarket, permissions } = useSelector((state: AppState) => state.authentication);
  const rightSection = composeRightSection(permissions, utcMarket);
  const { isUptime } = useUptime();
  const history = useHistory();
  const location = useLocation();
  const { redirectToDiamAccountPortal } = useDiamAccountPortalUrl();

  const menuCustomActions = useMemo<Partial<Record<MenuConstants, () => void>>>(() => {
    return {
      HOW_TO_VIDEOS: toggleYoutubePlaylistModalOpen,
      ACCOUNT: redirectToDiamAccountPortal,
    };
  }, [toggleYoutubePlaylistModalOpen, redirectToDiamAccountPortal]);

  const [activeMenuId, setActiveMenuId] = useState<number>();

  const [isMobileNavLeftOpen, toggleIsMobileNavLeftOpen, setIsMobileNavLeftOpen] =
    useBooleanState(false);

  const [isMobileNavRightOpen, toggleIsMobileNavRightOpen, setIsMobileNavRightOpen] =
    useBooleanState(false);
  const mobileView = useMediaQuery(`(max-width: ${breakpoints.l}px)`);

  const onClickMenuHandler = useCallback(
    (menuItem: FleetMenuItem) => {
      setActiveMenuId(menuItem.id);
      menuCustomActions[menuItem.metadata.key]?.();
      if (isLinkMenuItem(menuItem.metadata)) {
        history.push(menuItem.metadata.path);
      } else if (isOutLinkMenuItem(menuItem.metadata)) {
        window.open(menuItem.metadata.outPath);
      }
    },
    [history, menuCustomActions]
  );

  const onNavMenuClickHandler = useCallback(
    (menuItem: HudskitMenuItem) => {
      if (hasMenuMetadata(menuItem)) {
        onClickMenuHandler(menuItem);
      }
    },
    [onClickMenuHandler]
  );

  const onMobileMenuClickHandler = useCallback(
    (menuItem: FleetMenuItem) => {
      setIsMobileNavLeftOpen(false);
      setIsMobileNavRightOpen(false);
      onClickMenuHandler(menuItem);
    },
    [onClickMenuHandler, setIsMobileNavLeftOpen, setIsMobileNavRightOpen]
  );

  const buildMenuItems = useCallback((menus: readonly MenuItem[], startIndex: number) => {
    let menuCount = startIndex;
    const navMenuItems: FleetMenuItem[] = [];
    for (const menu of menus) {
      const navMenuItem = mapNavMenu(menu, menuCount);
      if (navMenuItem) {
        menuCount = navMenuItem.id + 1;
        navMenuItems.push(navMenuItem);
      }
    }
    return navMenuItems;
  }, []);

  const { data: greenkeeperEnabled, isLoaded: isGktFlagLoaded } = useGetGreenkeeperEnabled();

  const finalLeftSection = useMemo(() => {
    const result = [...leftSection];
    if (isGktFlagLoaded && greenkeeperEnabled && !isUptime) {
      result.push({ id: MenuConstants.GREENKEEPER });
    }
    return result;
  }, [leftSection, isGktFlagLoaded, isUptime, greenkeeperEnabled]);

  const leftMenuItems = useMemo<FleetMenuItem[]>(() => {
    return buildMenuItems(finalLeftSection, 0);
  }, [buildMenuItems, finalLeftSection]);

  const finalMobileLeftSection = useMemo<MenuItem[]>(
    () =>
      finalLeftSection.map((item) =>
        item.id === MenuConstants.GREENKEEPER
          ? { id: MenuConstants.GREENKEEPER, items: greenkeeperMobileSubmenu }
          : item
      ),
    [finalLeftSection]
  );

  const mobileLeftMenuItems = useMemo<FleetMenuItem[]>(() => {
    return buildMenuItems(finalMobileLeftSection, 0);
  }, [buildMenuItems, finalMobileLeftSection]);

  const rightMenuItems = useMemo<FleetMenuItem[]>(() => {
    return buildMenuItems(
      rightSection.filter((item) => !secondaryRightMenu.includes(item.id)),
      100
    );
  }, [buildMenuItems, rightSection]);
  const rightSectionMenuItems = useMemo<FleetMenuItem[]>(() => {
    return buildMenuItems(
      rightSection.filter((item) => secondaryRightMenu.includes(item.id)),
      200
    );
  }, [buildMenuItems, rightSection]);

  const secondaryMobileMenu = useMemo<FleetMenuItem[]>(
    () => rightSectionMenuItems.filter((item) => item.metadata.key === MenuConstants.INFORMATION),
    [rightSectionMenuItems]
  );

  const userMenu = useMemo<FleetMenuItem[] | undefined>(
    () =>
      rightSectionMenuItems.filter((item) => item.metadata.key === MenuConstants.USER)[0]
        ?.menuItems,
    [rightSectionMenuItems]
  );

  const navbarItems = useMemo(
    () => [...mobileLeftMenuItems, ...rightMenuItems],
    [mobileLeftMenuItems, rightMenuItems]
  );

  const activeMenuItems = useMemo(
    () => [
      ...(mobileView ? mobileLeftMenuItems : leftMenuItems),
      ...rightMenuItems,
      ...rightSectionMenuItems,
    ],
    [leftMenuItems, mobileLeftMenuItems, mobileView, rightMenuItems, rightSectionMenuItems]
  );

  const flatMenuItems = useMemo(() => {
    let menuAndSubmenuItems: FleetMenuItem[] = [];
    menuAndSubmenuItems = [...activeMenuItems];
    for (const menuItem of activeMenuItems) {
      if (menuItem.menuItems) {
        menuAndSubmenuItems = [...menuAndSubmenuItems, ...menuItem.menuItems];
      }
    }
    return menuAndSubmenuItems;
  }, [activeMenuItems]);

  useEffect(() => {
    const normalizedLocationPath = trimUptimePath(location.pathname);

    const getMatchRank = (item: FleetMenuItem) => {
      const path = menuItemPath(item);

      if (!path) {
        return -1;
      }

      if (path === location.pathname) {
        return 2;
      }

      if (!item.metadata.isOnlyActiveWhenMatchingAll) {
        const indexOfSubPath = path.indexOf('/', 1);
        if (indexOfSubPath > -1) {
          const basePath = trimUptimePath(path.substring(0, indexOfSubPath));
          if (normalizedLocationPath.startsWith(basePath)) {
            return 1;
          }
        }

        const locationPathname = trimUptimePath(location.pathname);
        const itemPathname = trimUptimePath(path);
        if (locationPathname.startsWith(itemPathname.concat('/'))) {
          return 1;
        }
      }

      return -1;
    };

    const activeMenuItem = flatMenuItems.reduce<FleetMenuItem | undefined>((bestMatch, item) => {
      const itemRank = getMatchRank(item);

      if (itemRank < 0) {
        return bestMatch;
      }

      if (!bestMatch) {
        return item;
      }

      const bestMatchRank = getMatchRank(bestMatch);
      if (itemRank > bestMatchRank) {
        return item;
      }

      if (itemRank === bestMatchRank) {
        const bestMatchPathLength = menuItemPath(bestMatch)?.length ?? 0;
        const itemPathLength = menuItemPath(item)?.length ?? 0;

        if (itemPathLength > bestMatchPathLength) {
          return item;
        }
      }

      return bestMatch;
    }, undefined);

    if (activeMenuItem) {
      setActiveMenuId(activeMenuItem.id);
    }
  }, [location.pathname, flatMenuItems]);

  useEffect(() => {
    if (isMobileNavLeftOpen) {
      setIsMobileNavRightOpen(false);
    }
  }, [isMobileNavLeftOpen, setIsMobileNavRightOpen]);

  useEffect(() => {
    if (isMobileNavRightOpen) {
      setIsMobileNavLeftOpen(false);
    }
  }, [isMobileNavRightOpen, setIsMobileNavLeftOpen]);

  const { data: measurementSystemSelectionEnabled } = useGetMeasurementSystemPreferenceEnabled();

  const handleUserPreferencesSubmenuClick = useCallback(
    (menuItem: HudskitMenuItem) => {
      if (hasMenuMetadata(menuItem)) {
        onMobileMenuClickHandler(menuItem as FleetMenuItem);
      } else {
        // For MenuItem objects from hfs-common-ui that don't have metadata
        setIsMobileNavLeftOpen(false);
        setIsMobileNavRightOpen(false);
      }
    },
    [onMobileMenuClickHandler, setIsMobileNavLeftOpen, setIsMobileNavRightOpen]
  );

  const renderDropDownMenu = (menuItem: FleetMenuItem) => {
    return (
      <DropdownNavMenu
        menuItem={menuItem}
        activeId={activeMenuId}
        onClick={onClickMenuHandler}
        key={menuItem.id}
      />
    );
  };
  return (
    <NavBar
      logoElement={<NavbarLogo />}
      primaryArea={
        !mobileView && (
          <S.Nav>
            <NavMenu
              onClick={onNavMenuClickHandler}
              menu={leftMenuItems}
              activeMenuItemId={activeMenuId}
            />
            <S.RightSideMultiMenus>
              <NavMenu
                onClick={onNavMenuClickHandler}
                menu={rightMenuItems}
                activeMenuItemId={activeMenuId}
              />
              <UptimeCompanyInfo />
            </S.RightSideMultiMenus>
          </S.Nav>
        )
      }
      rightSideArea={
        <>
          {!mobileView && (
            <S.DesktopRightSideMenu>
              <DevScreenSizeIcon />
              {rightSectionMenuItems.map(renderDropDownMenu)}
            </S.DesktopRightSideMenu>
          )}
          <S.MobileRightSideMenu>
            {mobileView && !isUptime && (
              <MobileMenuDrawer
                isOpen={isMobileNavRightOpen}
                toggleOpen={toggleIsMobileNavRightOpen}
                position="right"
                icon="action.avatar_outlined"
                iconActive="action.avatar"
              >
                {userMenu && !measurementSystemSelectionEnabled && (
                  <MobileRightMenu
                    menu={userMenu}
                    activeMenuId={activeMenuId}
                    onClick={onMobileMenuClickHandler}
                  />
                )}
                {userMenu && measurementSystemSelectionEnabled && (
                  <UserPreferencesSubmenu
                    onSubmenuClick={handleUserPreferencesSubmenuClick}
                    menuItems={userMenu}
                  />
                )}
              </MobileMenuDrawer>
            )}
          </S.MobileRightSideMenu>
        </>
      }
      leftSideArea={
        mobileView && (
          <MobileMenuDrawer
            isOpen={isMobileNavLeftOpen}
            toggleOpen={toggleIsMobileNavLeftOpen}
            isAvatarIcon={false}
          >
            <UptimeCompanyInfo />
            <MobileMenu
              activeMenuId={activeMenuId}
              menu={navbarItems}
              onClick={onMobileMenuClickHandler}
            />
            <MobileMenu
              activeMenuId={activeMenuId}
              menu={secondaryMobileMenu}
              onClick={onMobileMenuClickHandler}
              mainMenu={false}
            />
          </MobileMenuDrawer>
        )
      }
    />
  );
};

const mapDispatchToProps: DispatchProps = {
  toggleYoutubePlaylistModalOpen: toggleYoutubePlaylistModalOpenAction,
};

function mapStateToProps({ menu }: AppState): StateProps {
  const { leftSection } = menu;

  return {
    leftSection,
  };
}

export default withRouter(
  connect<StateProps, DispatchProps, RouteComponentProps, AppState>(
    mapStateToProps,
    mapDispatchToProps
  )(MenubarContainer)
);
